import { ChangeEvent, FC, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { FormSelect } from "components/inputs/FormSelect"
import { TopBar } from "components/navigation/TopBar"
import { PRIZE_POOL_TYPE_OPTIONS, REFERRAL_REWARD_TYPE_OPTIONS, SCORING_MODE_OPTIONS } from "components/tournaments/constants"
import { GameFilterSelect, GameFilterValue } from "components/tournaments/GameFilterSelect"
import { PointRulesBuilder } from "components/tournaments/point-rules/PointRulesBuilder"
import { PrizeDistributionBuilder } from "components/tournaments/PrizeDistributionBuilder"
import { SyntheticFillersBuilder, SyntheticFillersValue } from "components/tournaments/SyntheticFillersBuilder"
import { TermsAndConditionsBuilder } from "components/tournaments/TermsAndConditionsBuilder"

import { URL } from "constants/api/requests"

import { PaymentServiceAdmin } from "services/payment/payment.service"
import { settingsService } from "services/settings/settings.service"
import { TournamentService } from "services/tournaments/tournament.service"

import {
  GameFilterType,
  PointRule,
  PrizeDistributionEntry,
  PrizePoolType,
  ReferralRewardType,
  TournamentScoringMode
} from "types/tournament.types"

import { getCurrencySymbol } from "util/formatter"
import { emptyHtmlToNull } from "util/htmlHelpers"

const FALLBACK_CURRENCY_OPTIONS = ["USD"]

export const TournamentEdit: FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation("translation", { useSuspense: false })

  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [currencyOptions, setCurrencyOptions] = useState<string[]>(FALLBACK_CURRENCY_OPTIONS)
  const [usdRate, setUsdRate] = useState<number | null>(null)
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    start_time: "",
    end_time: "",
    entry_deadline_minutes: "",
    scoring_mode: "POINTS" as TournamentScoringMode,
    currency: "USD",
    enable_chat: false,
    chat_slow_mode_seconds: "",
    min_bet: "",
    max_bet: "",
    prize_pool_type: "FIXED" as PrizePoolType,
    prize_pool_base: "",
    max_participants: "",
    min_participants: "",
    entry_fee: "",
    min_deposit_required: "",
    game_filter_type: "ALL" as GameFilterType,
    game_filter_ids: [] as string[],
    provider_filter_ids: [] as string[],
    enable_referrals: false,
    referral_max_invites: "",
    referral_reward_type: "FIXED_PER_INVITE" as ReferralRewardType,
    referral_reward_value: "",
    point_rules: [] as PointRule[],
    prize_distribution: [] as PrizeDistributionEntry[],
    auto_distribute_prizes: false,
    terms_and_conditions: null as string | null,
    synthetic_count: "",
    synthetic_score_min: "",
    synthetic_score_max: "",
    synthetic_name_mode: "ANONYMOUS" as SyntheticFillersValue["synthetic_name_mode"],
    synthetic_name_pool: ""
  })

  useEffect(() => {
    const loadTournament = async () => {
      if (!id) return

      try {
        const tournament = await TournamentService.getTournament(id)

        if (!tournament) {
          toast.error("Tournament not found!")
          navigate(URL.TOURNAMENTS)
          return
        }

        if (tournament.status !== "DRAFT" && tournament.status !== "SCHEDULED") {
          toast.error("Only draft or scheduled tournaments can be edited!")
          navigate(URL.TOURNAMENT.replace(":id", id))
          return
        }

        // Convert dates to datetime-local format
        const startTime = new Date(tournament.start_time).toISOString().slice(0, 16)
        const endTime = new Date(tournament.end_time).toISOString().slice(0, 16)

        // Money inputs are in the tournament currency's minor units. Prefer the admin-entered
        // native value (display_amounts); fall back to the stored column for legacy USD rows.
        const displayAmounts = ((tournament as any).display_amounts || {}) as Record<string, any>
        const nativeMinorField = (field: string): string => {
          const nativeValue = displayAmounts[field] ?? (tournament as any)[field]
          return nativeValue !== null && nativeValue !== undefined ? String(nativeValue) : ""
        }
        const nativePrizeDistribution = Array.isArray(tournament.prize_distribution)
          ? tournament.prize_distribution.map((entry: any) => {
              const usdAmount = entry?.amount
              if (usdAmount !== null && usdAmount !== undefined && Array.isArray(displayAmounts.prize_distribution)) {
                const nativeEntry = displayAmounts.prize_distribution.find((candidate: any) => candidate.rank === entry.rank)
                if (nativeEntry?.amount !== null && nativeEntry?.amount !== undefined) return { ...entry, amount: nativeEntry.amount }
              }
              return entry
            })
          : tournament.prize_distribution || []

        setFormData({
          name: tournament.name,
          description: tournament.description || "",
          start_time: startTime,
          end_time: endTime,
          entry_deadline_minutes: (tournament as any).entry_deadline_minutes ? String((tournament as any).entry_deadline_minutes) : "",
          scoring_mode: tournament.scoring_mode,
          currency: tournament.currency || "USD",
          enable_chat: (tournament as any).enable_chat || false,
          chat_slow_mode_seconds: (tournament as any).chat_slow_mode_seconds ? String((tournament as any).chat_slow_mode_seconds) : "",
          // opt_in_required: true, // Always require opt-in (registration required)
          // Money values are in the tournament currency's minor units (native)
          min_bet: nativeMinorField("min_bet_per_spin"),
          max_bet: nativeMinorField("max_bet_per_spin"),
          prize_pool_type: tournament.prize_pool_type || "FIXED",
          prize_pool_base: nativeMinorField("prize_pool_base"),
          max_participants: tournament.max_participants ? String(tournament.max_participants) : "",
          min_participants: (tournament as any).min_participants ? String((tournament as any).min_participants) : "",
          entry_fee: nativeMinorField("entry_fee"),
          min_deposit_required: nativeMinorField("min_deposit_required"),
          game_filter_type: (tournament as any).game_filter_type || "ALL",
          game_filter_ids:
            (tournament as any).game_filter_ids && Array.isArray((tournament as any).game_filter_ids)
              ? (tournament as any).game_filter_ids
              : [],
          provider_filter_ids:
            (tournament as any).provider_filter_ids && Array.isArray((tournament as any).provider_filter_ids)
              ? (tournament as any).provider_filter_ids
              : [],
          enable_referrals: (tournament as any).enable_referrals || false,
          referral_max_invites: (tournament as any).referral_max_invites ? String((tournament as any).referral_max_invites) : "",
          referral_reward_type: (tournament as any).referral_reward_type || "FIXED_PER_INVITE",
          referral_reward_value: (tournament as any).referral_reward_value ? String((tournament as any).referral_reward_value) : "",
          point_rules: tournament.point_rules || [],
          prize_distribution: nativePrizeDistribution,
          auto_distribute_prizes: (tournament as any).auto_distribute_prizes || false,
          terms_and_conditions: (tournament as any).terms_and_conditions || null,
          synthetic_count: (tournament as any).synthetic_count ? String((tournament as any).synthetic_count) : "",
          synthetic_score_min:
            (tournament as any).synthetic_score_min !== null && (tournament as any).synthetic_score_min !== undefined
              ? String((tournament as any).synthetic_score_min)
              : "",
          synthetic_score_max:
            (tournament as any).synthetic_score_max !== null && (tournament as any).synthetic_score_max !== undefined
              ? String((tournament as any).synthetic_score_max)
              : "",
          synthetic_name_mode: ((tournament as any).synthetic_name_mode as SyntheticFillersValue["synthetic_name_mode"]) || "ANONYMOUS",
          synthetic_name_pool: Array.isArray((tournament as any).synthetic_name_pool)
            ? (tournament as any).synthetic_name_pool.join("\n")
            : ""
        })
      } catch (error) {
        toast.error("Failed to load tournament!")
        navigate(URL.TOURNAMENTS)
      } finally {
        setLoadingData(false)
      }
    }

    loadTournament()
  }, [id, navigate])

  // Load the tenant's enabled fiat currencies for the display-currency selector
  useEffect(() => {
    settingsService.getAll().then((settings) => {
      if (settings?.rcoin) {
        const enabled = Object.entries(settings.rcoin)
          .filter(([, on]) => on)
          .map(([coin]) => coin)
          .sort()
        if (enabled.length > 0) setCurrencyOptions(enabled)
      }
    })
  }, [])

  // Fetch the USD conversion rate for the selected currency (for the "≈ $" hint only)
  useEffect(() => {
    if (!formData.currency || formData.currency === "USD") {
      setUsdRate(null)
      return
    }
    let active = true
    PaymentServiceAdmin.getCurRates(formData.currency).then((res) => {
      if (active) setUsdRate(res?.rates?.USD ?? null)
    })
    return () => {
      active = false
    }
  }, [formData.currency])

  // Render a money-input hint: native amount, plus an approximate USD value for non-USD currencies
  const amountHint = (value: string) => {
    if (!value) return null
    const major = parseFloat(value) / 100
    const usd = usdRate && formData.currency !== "USD" ? ` ≈ $${(major * usdRate).toFixed(2)}` : ""
    return (
      <p className="text-xs text-cyan-500 dark:text-cyan-400 mt-1 font-medium">
        = {getCurrencySymbol(formData.currency)}
        {major.toFixed(2)}
        {usd}
      </p>
    )
  }

  const handleChange = (e: any) => {
    const { name, value, type } = e.target

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked
      setFormData((prev) => ({ ...prev, [name]: checked }))
    } else {
      setFormData((prev) => {
        const updated = { ...prev, [name]: value }
        // Reset progressive prize pool type when entry fee is cleared
        if (name === "entry_fee" && updated.prize_pool_type === "PROGRESSIVE" && (!value || parseInt(value) <= 0)) {
          updated.prize_pool_type = "FIXED"
        }
        return updated
      })
    }
  }

  const handlePointRulesChange = (rules: PointRule[]) => {
    setFormData((prev) => ({ ...prev, point_rules: rules }))
  }

  const handlePrizeDistributionChange = (distribution: PrizeDistributionEntry[]) => {
    setFormData((prev) => ({ ...prev, prize_distribution: distribution }))
  }

  const handleTermsChange = (html: string) => {
    setFormData((prev) => ({ ...prev, terms_and_conditions: emptyHtmlToNull(html) }))
  }

  const handleSyntheticChange = (next: SyntheticFillersValue) => {
    setFormData((prev) => ({
      ...prev,
      synthetic_count: next.synthetic_count,
      synthetic_score_min: next.synthetic_score_min,
      synthetic_score_max: next.synthetic_score_max,
      synthetic_name_mode: next.synthetic_name_mode,
      synthetic_name_pool: next.synthetic_name_pool
    }))
  }

  const handlePrizePoolTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    if (value === "PROGRESSIVE" && (!formData.entry_fee || parseInt(formData.entry_fee) <= 0)) {
      toast.error(t("tournaments.form.progressiveEntryFeeError") as string)
      return
    }
    // When switching to PROGRESSIVE, convert any fixed-amount distribution entries to percentage
    if (value === "PROGRESSIVE" && formData.prize_distribution.length > 0) {
      const converted = formData.prize_distribution.map((entry) => {
        if (entry.amount !== undefined) {
          const { amount: _amount, ...rest } = entry
          return { ...rest, percentage: 0 }
        }
        return entry
      })
      setFormData((prev) => ({ ...prev, prize_pool_type: value, prize_distribution: converted }))
      return
    }
    handleChange(e)
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Validate dates
      if (new Date(formData.start_time) >= new Date(formData.end_time)) {
        toast.error("End time must be after start time!")
        setLoading(false)
        return
      }

      // A non-USD tournament needs a known exchange rate to convert its amounts; if we
      // couldn't fetch one, don't let the admin save it in anything other than USD.
      if (formData.currency !== "USD" && !usdRate) {
        toast.error(t("tournaments.form.currencyRateUnavailable") as string)
        setLoading(false)
        return
      }

      // Build payload
      const payload: any = {
        name: formData.name,
        description: formData.description || null,
        start_time: formData.start_time ? new Date(formData.start_time + "Z").toISOString() : "",
        end_time: formData.end_time ? new Date(formData.end_time + "Z").toISOString() : "",
        entry_deadline_minutes: formData.entry_deadline_minutes ? parseInt(formData.entry_deadline_minutes) : null,
        scoring_mode: formData.scoring_mode,
        currency: formData.currency || "USD",
        enable_chat: formData.enable_chat,
        chat_slow_mode_seconds: formData.chat_slow_mode_seconds ? parseInt(formData.chat_slow_mode_seconds) : 0,
        min_bet_per_spin: formData.min_bet ? parseInt(formData.min_bet) : null,
        max_bet_per_spin: formData.max_bet ? parseInt(formData.max_bet) : null,
        prize_pool_type: formData.prize_pool_type || null,
        prize_pool_base: formData.prize_pool_base ? parseInt(formData.prize_pool_base) : null,
        max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
        min_participants: formData.min_participants ? parseInt(formData.min_participants) : null,
        entry_fee: formData.entry_fee ? parseInt(formData.entry_fee) : null,
        min_deposit_required: formData.min_deposit_required ? parseInt(formData.min_deposit_required) : null,
        point_rules: formData.point_rules,
        prize_distribution: formData.prize_distribution,
        auto_distribute_prizes: formData.auto_distribute_prizes,
        terms_and_conditions: formData.terms_and_conditions,
        enable_referrals: formData.enable_referrals,
        referral_max_invites: formData.referral_max_invites ? parseInt(formData.referral_max_invites) : null,
        referral_reward_type: formData.enable_referrals ? formData.referral_reward_type : null,
        referral_reward_value: formData.referral_reward_value ? parseInt(formData.referral_reward_value) : null,
        synthetic_count: formData.synthetic_count ? parseInt(formData.synthetic_count) : 0,
        synthetic_score_min: formData.synthetic_score_min ? parseFloat(formData.synthetic_score_min) : null,
        synthetic_score_max: formData.synthetic_score_max ? parseFloat(formData.synthetic_score_max) : null,
        synthetic_name_mode: formData.synthetic_count && parseInt(formData.synthetic_count) > 0 ? formData.synthetic_name_mode : null,
        synthetic_name_pool:
          formData.synthetic_name_mode === "CUSTOM_POOL"
            ? formData.synthetic_name_pool
                .split(/[\n,]/)
                .map((s) => s.trim())
                .filter(Boolean)
            : null
      }

      // Game filter
      payload.game_filter_type = formData.game_filter_type || "ALL"
      payload.game_filter_ids = formData.game_filter_type === "SPECIFIC_GAMES" ? formData.game_filter_ids : []
      payload.provider_filter_ids = formData.game_filter_type === "PROVIDER" ? formData.provider_filter_ids : []

      await TournamentService.updateTournament(id!, payload)

      toast.success("Tournament updated successfully!")
      navigate(URL.TOURNAMENTS)
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to update tournament!")
    } finally {
      setLoading(false)
    }
  }

  if (loadingData) {
    return (
      <div className="w-full max-w-[100vw] p-4 pt-0 sm:p-8 sm:pt-0">
        <TopBar title={t("pages.tournamentEdit.title")} />
        <div className="flex items-center justify-center py-16">
          <span className="loading loading-spinner loading-lg" />
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-[100vw] p-4 pt-0 sm:p-8 sm:pt-0">
      <TopBar title={t("pages.tournamentEdit.title")} />

      <div className="mt-4">
        <div className="p-6 bg-white dark:bg-[#0C0C1A] rounded-xl border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] shadow-sm">
          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{t("tournaments.form.basicInfo")}</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("tournaments.form.tournamentName")}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                    placeholder={t("tournaments.form.namePlaceholder")}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("tournaments.form.description")}
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                    placeholder={t("tournaments.form.descriptionPlaceholder")}
                  />
                </div>

                <FormSelect
                  name="currency"
                  label={t("tournaments.form.currency")}
                  value={formData.currency}
                  onChange={handleChange}
                  options={currencyOptions.map((c) => ({ value: c, label: `${c} (${getCurrencySymbol(c)})` }))}
                  helpText={t("tournaments.form.currencyHelp")}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t("tournaments.form.startTime")}
                    </label>
                    <input
                      type="datetime-local"
                      name="start_time"
                      value={formData.start_time}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t("tournaments.form.endTime")}
                    </label>
                    <input
                      type="datetime-local"
                      name="end_time"
                      value={formData.end_time}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("tournaments.form.entryDeadline")}
                  </label>
                  <input
                    type="number"
                    name="entry_deadline_minutes"
                    value={formData.entry_deadline_minutes}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                    placeholder={t("tournaments.form.entryDeadlinePlaceholder")}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("tournaments.form.entryDeadlineHelp")}</p>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      name="enable_chat"
                      checked={formData.enable_chat}
                      onChange={handleChange}
                      className="w-4 h-4 rounded border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] text-[rgba(139,92,246,1)] focus:ring-[rgba(139,92,246,0.5)] focus:ring-2"
                    />
                    {t("tournaments.form.enableChat")}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("tournaments.form.enableChatHelp")}</p>
                </div>

                {formData.enable_chat && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t("tournaments.form.slowMode")}
                    </label>
                    <input
                      type="number"
                      name="chat_slow_mode_seconds"
                      value={formData.chat_slow_mode_seconds}
                      onChange={handleChange}
                      min="0"
                      className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                      placeholder={t("tournaments.form.slowModePlaceholder")}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("tournaments.form.slowModeHelp")}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Scoring Configuration */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{t("tournaments.form.scoringConfig")}</h3>

              <div className="space-y-4">
                <FormSelect
                  name="scoring_mode"
                  label={t("tournaments.form.scoringMode")}
                  value={formData.scoring_mode}
                  onChange={handleChange}
                  options={SCORING_MODE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                />

                {formData.scoring_mode === "POINTS" && <PointRulesBuilder value={formData.point_rules} onChange={handlePointRulesChange} />}
              </div>
            </div>

            {/* Eligibility */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{t("tournaments.form.eligibility")}</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("tournaments.form.minBet")}</label>
                  <input
                    type="number"
                    name="min_bet"
                    value={formData.min_bet}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                    placeholder="100"
                  />
                  {amountHint(formData.min_bet)}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("tournaments.form.minBetHelp")}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("tournaments.form.maxBet")}</label>
                  <input
                    type="number"
                    name="max_bet"
                    value={formData.max_bet}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                    placeholder={t("tournaments.form.noLimit")}
                  />
                  {amountHint(formData.max_bet)}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("tournaments.form.maxBetHelp")}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("tournaments.form.maxParticipants")}
                  </label>
                  <input
                    type="number"
                    name="max_participants"
                    value={formData.max_participants}
                    onChange={handleChange}
                    min="1"
                    className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                    placeholder={t("tournaments.form.unlimited")}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("tournaments.form.maxParticipantsHelp")}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("tournaments.form.minParticipants")}
                  </label>
                  <input
                    type="number"
                    name="min_participants"
                    value={formData.min_participants}
                    onChange={handleChange}
                    min="1"
                    className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                    placeholder={t("tournaments.form.optional")}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("tournaments.form.minParticipantsHelp")}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("tournaments.form.entryFee")}
                  </label>
                  <input
                    type="number"
                    name="entry_fee"
                    value={formData.entry_fee}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                    placeholder="500"
                  />
                  {amountHint(formData.entry_fee)}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("tournaments.form.entryFeeHelp")}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("tournaments.form.minDepositRequired")}
                  </label>
                  <input
                    type="number"
                    name="min_deposit_required"
                    value={formData.min_deposit_required}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                    placeholder="1000"
                  />
                  {amountHint(formData.min_deposit_required)}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("tournaments.form.minDepositHelp")}</p>
                </div>

                <GameFilterSelect
                  value={{
                    game_filter_type: formData.game_filter_type,
                    game_filter_ids: formData.game_filter_ids,
                    provider_filter_ids: formData.provider_filter_ids
                  }}
                  onChange={(v: GameFilterValue) =>
                    setFormData((prev) => ({
                      ...prev,
                      game_filter_type: v.game_filter_type,
                      game_filter_ids: v.game_filter_ids,
                      provider_filter_ids: v.provider_filter_ids
                    }))
                  }
                />
              </div>
            </div>

            {/* Referral Configuration */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{t("tournaments.form.referralProgram")}</h3>

              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      name="enable_referrals"
                      checked={formData.enable_referrals}
                      onChange={handleChange}
                      className="w-4 h-4 rounded border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] text-[rgba(139,92,246,1)] focus:ring-[rgba(139,92,246,0.5)] focus:ring-2"
                    />
                    {t("tournaments.form.enableReferralProgram")}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("tournaments.form.enableReferralHelp")}</p>
                </div>

                {formData.enable_referrals && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t("tournaments.form.maxInvitesPerPlayer")}
                      </label>
                      <input
                        type="number"
                        name="referral_max_invites"
                        value={formData.referral_max_invites}
                        onChange={handleChange}
                        min="1"
                        className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                        placeholder={t("tournaments.form.unlimited")}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("tournaments.form.maxInvitesHelp")}</p>
                    </div>

                    <FormSelect
                      name="referral_reward_type"
                      label={t("tournaments.form.rewardType")}
                      value={formData.referral_reward_type}
                      onChange={handleChange}
                      options={REFERRAL_REWARD_TYPE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {formData.referral_reward_type === "PERCENTAGE"
                          ? t("tournaments.form.rewardPercentage")
                          : t("tournaments.form.rewardPoints")}
                      </label>
                      <input
                        type="number"
                        name="referral_reward_value"
                        value={formData.referral_reward_value}
                        onChange={handleChange}
                        min="0"
                        className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                        placeholder={formData.referral_reward_type === "PERCENTAGE" ? "e.g., 10" : "e.g., 50"}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {formData.referral_reward_type === "FIXED_PER_INVITE"
                          ? t("tournaments.form.rewardFixedPerInviteHelp")
                          : formData.referral_reward_type === "FIXED_PER_ACTION"
                          ? t("tournaments.form.rewardFixedPerActionHelp")
                          : t("tournaments.form.rewardPercentageHelp")}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Prize Pool */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{t("tournaments.form.prizePool")}</h3>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t("tournaments.form.prizePoolType")}
                    </label>
                    <select
                      name="prize_pool_type"
                      value={formData.prize_pool_type}
                      onChange={handlePrizePoolTypeChange}
                      className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                    >
                      {PRIZE_POOL_TYPE_OPTIONS.map((option) => (
                        <option
                          key={option.value || "none"}
                          value={option.value}
                          disabled={option.value === "PROGRESSIVE" && (!formData.entry_fee || parseInt(formData.entry_fee) <= 0)}
                        >
                          {option.label}
                          {option.value === "PROGRESSIVE" && (!formData.entry_fee || parseInt(formData.entry_fee) <= 0)
                            ? t("tournaments.form.requiresEntryFee")
                            : ""}
                        </option>
                      ))}
                    </select>
                    {formData.prize_pool_type === "PROGRESSIVE" && (
                      <p className="text-xs text-amber-500 dark:text-amber-400 mt-1">{t("tournaments.form.progressiveNote")}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t("tournaments.form.prizePoolBaseAmount")}
                    </label>
                    <input
                      type="number"
                      name="prize_pool_base"
                      value={formData.prize_pool_base}
                      onChange={handleChange}
                      min="0"
                      className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                      placeholder="500000"
                    />
                    {amountHint(formData.prize_pool_base)}
                  </div>
                </div>

                <PrizeDistributionBuilder
                  value={formData.prize_distribution}
                  onChange={handlePrizeDistributionChange}
                  prizePoolBase={formData.prize_pool_base}
                  forcePercentageOnly={formData.prize_pool_type === "PROGRESSIVE"}
                  currency={formData.currency}
                />

                <div className="flex items-center gap-3 mt-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="auto_distribute_prizes"
                      checked={formData.auto_distribute_prizes}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-violet-600" />
                  </label>
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t("tournaments.form.autoDistributePrizes")}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t("tournaments.form.autoDistributeHelp")}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Synthetic Leaderboard Fillers */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-1 text-gray-900 dark:text-white">
                {t("tournaments.form.syntheticFillers.sectionTitle")}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t("tournaments.form.syntheticFillers.sectionHelp")}</p>
              <SyntheticFillersBuilder
                value={{
                  synthetic_count: formData.synthetic_count,
                  synthetic_score_min: formData.synthetic_score_min,
                  synthetic_score_max: formData.synthetic_score_max,
                  synthetic_name_mode: formData.synthetic_name_mode,
                  synthetic_name_pool: formData.synthetic_name_pool
                }}
                onChange={handleSyntheticChange}
              />
            </div>

            {/* Terms & Conditions */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{t("tournaments.form.termsAndConditions")}</h3>
              <TermsAndConditionsBuilder value={formData.terms_and_conditions} onChange={handleTermsChange} />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)]">
              <button
                type="button"
                onClick={() => navigate(URL.TOURNAMENTS)}
                className="px-6 py-2 border border-[rgba(139,92,246,0.8)] dark:border-[rgba(139,92,246,0.5)] rounded-lg text-[rgba(139,92,246,1)] dark:text-white hover:bg-[rgba(139,92,246,0.1)] dark:hover:bg-[rgba(139,92,246,0.1)] transition-all"
                disabled={loading}
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-[rgba(139,92,246,1)] text-white rounded-lg hover:bg-[rgba(139,92,246,0.8)] disabled:opacity-50 transition-all"
                disabled={loading}
              >
                {loading ? t("tournaments.form.updating") : t("tournaments.form.updateTournament")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
